"use client";

import { useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade } from "swiper/modules";
import { buildContentstackImageUrl } from "@/lib/utils";
import { useGalleryImages } from "./useGalleryImages";
import "swiper/css";
import "swiper/css/effect-fade";

const MOBILE_MAX_WIDTH = 900;

const desktopTile = (index) =>
  index % 3 === 0
    ? {
        patternClass: "col-span-2 aspect-[16/12]",
        sizes: "(max-width: 1023px) 0px, 900px",
        maxWidth: 1200,
      }
    : {
        patternClass: "col-span-1 aspect-[4/3]",
        sizes: "(max-width: 1023px) 0px, 450px",
        maxWidth: 960,
      };

const IMG_LAZY = { fetchPriority: "low", loading: "lazy" };
const IMG_PRELOAD = { priority: true, fetchPriority: "high" };

const shouldLoad = (index, active, total) =>
  total <= 1 || index === active || index === (active + 1) % total;

const ProductGallery = ({ images: imagesProp = [] }) => {
  const [active, setActive] = useState(0);
  const syncSlide = (swiper) => setActive(swiper.realIndex);
  const { images, galleryKey } = useGalleryImages(imagesProp);

  if (!images.length) return null;

  return (
    <div>
      {/* Mobile — full-bleed Swiper carousel */}
      <div className="lg:hidden relative aspect-[4/3]">
        <Swiper
          key={galleryKey}
          modules={[EffectFade]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          slidesPerView={1}
          loop={images.length > 2}
          onSwiper={syncSlide}
          onSlideChange={syncSlide}
          className="h-full"
        >
          {images.map((image, index) => (
            <SwiperSlide key={image.uid || index}>
              <div className="relative h-full bg-white">
                {shouldLoad(index, active, images.length) && (
                  <Image
                    src={buildContentstackImageUrl(image.url, MOBILE_MAX_WIDTH)}
                    alt={image.alt || `Gallery image ${index + 1}`}
                    fill
                    sizes="(max-width: 1023px) 100vw, 1px"
                    className="object-cover"
                    {...(index === 0 ? IMG_PRELOAD : IMG_LAZY)}
                  />
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Desktop — mosaic grid */}
      <div className="hidden lg:grid grid-cols-2 gap-4 auto-rows-auto">
        {images.map((image, index) => {
          const tile = desktopTile(index);
          return (
            <div
              key={image.uid || index}
              className={`relative overflow-hidden bg-white ${tile.patternClass}`}
            >
              <Image
                src={buildContentstackImageUrl(image.url, tile.maxWidth)}
                alt={image.alt || `Gallery image ${index + 1}`}
                fill
                sizes={tile.sizes}
                className="object-cover"
                {...(index === 0 ? IMG_PRELOAD : IMG_LAZY)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductGallery;
